import { SERVER_URL } from '../core/config';
/**
 * AnalyticsClient.js — Tracker product-analytics first-party (SPEC-2026-07-03, increment 2).
 *
 * Émet les events front (page_view, vues produit, panier, recherche…) vers
 * POST {serverURL}/analytics/collect, en clair (hors pipeline AES-GCM), non bloquant.
 *
 * Identité : anonymous_id persistant (localStorage) + session_id (30 min d'inactivité).
 * Acquisition : UTM + referrer capturés à l'entrée, persistés pour la session.
 * RGPD 2 niveaux : par défaut mode ANONYMISÉ (exempté CNIL) ; le full-fidelity
 * (empreinte + IP complète côté serveur) n'est activé qu'après consentement explicite
 * (`ev_analytics_consent = 'full'`). Le serveur ré-applique la dégradation par sécurité.
 *
 * DRY §5 : point d'émission UNIQUE côté client. Aucun fetch analytics ailleurs.
 */

const serverURL = SERVER_URL || '';
const ENDPOINT = `${serverURL}/analytics/collect`;

const ANON_KEY = 'ev_anon_id';
const SESSION_KEY = 'ev_session';
const UTM_KEY = 'ev_utm';
const CONSENT_KEY = 'ev_analytics_consent'; // 'full' | 'anon' | absent
const SESSION_TTL_MS = 30 * 60 * 1000;      // 30 min d'inactivité
const FLUSH_MS = 4000;
const BATCH_MAX = 20;

let queue = [];
let timer = null;

// ── Stockage sûr (localStorage peut lever en navigation privée) ──────────────
function lsGet(k) { try { return window.localStorage.getItem(k); } catch (_) { return null; } }
function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (_) { /* noop */ } }

function uuid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* fallback */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Identité ────────────────────────────────────────────────────────────────
function anonymousId() {
  let id = lsGet(ANON_KEY);
  if (!id) { id = uuid(); lsSet(ANON_KEY, id); }
  return id;
}

function sessionId() {
  const now = Date.now();
  let s = null;
  try { s = JSON.parse(lsGet(SESSION_KEY) || 'null'); } catch (_) { s = null; }
  if (!s || !s.id || (now - (s.last || 0)) > SESSION_TTL_MS) {
    s = { id: uuid(), start: now, last: now };
  } else {
    s.last = now;
  }
  lsSet(SESSION_KEY, JSON.stringify(s));
  return s.id;
}

// ── Acquisition (UTM + referrer), capturés une fois par session ──────────────
function captureAcquisition() {
  try {
    const existing = JSON.parse(lsGet(UTM_KEY) || 'null');
    const params = new URLSearchParams(window.location.search);
    const hasUtm = ['utm_source', 'utm_medium', 'utm_campaign', 'ref'].some((k) => params.get(k));
    if (existing && !hasUtm) return existing;

    const ref = document.referrer || '';
    let refDomain = '';
    try { refDomain = ref ? new URL(ref).hostname.replace(/^www\./, '') : ''; } catch (_) { refDomain = ''; }
    const sameHost = refDomain && refDomain === window.location.hostname.replace(/^www\./, '');

    // Résolution source/medium : UTM > referrer > direct.
    let source = params.get('utm_source') || '';
    let medium = params.get('utm_medium') || '';
    if (!source) {
      if (refDomain && !sameHost) {
        source = refDomain;
        medium = /google|bing|yahoo|duckduckgo|qwant|ecosia/i.test(refDomain) ? 'organic' : 'referral';
      } else if (!refDomain) {
        source = 'direct'; medium = 'none';
      }
    }
    const acq = {
      source,
      medium,
      campaign: params.get('utm_campaign') || '',
      term: params.get('utm_term') || '',
      content: params.get('utm_content') || '',
      referrer: sameHost ? '' : ref,
      referrer_domain: sameHost ? '' : refDomain,
      landing_page: existing && existing.landing_page ? existing.landing_page : window.location.pathname,
    };
    lsSet(UTM_KEY, JSON.stringify(acq));
    return acq;
  } catch (_) {
    return {};
  }
}

// ── Consentement ─────────────────────────────────────────────────────────────
export function getConsent() { return lsGet(CONSENT_KEY); }
export function setConsent(level) {
  // level: 'full' | 'anon'. Stocké avec horodatage (re-consentement 6 mois côté UI).
  lsSet(CONSENT_KEY, level);
  lsSet(`${CONSENT_KEY}_at`, String(Date.now()));
}

// Empreinte device légère (uniquement transmise si consentement full).
function deviceFp() {
  try {
    const s = `${navigator.userAgent}|${window.screen.width}x${window.screen.height}|${navigator.language}|${new Date().getTimezoneOffset()}`;
    let h = 0;
    for (let i = 0; i < s.length; i += 1) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
    return `fp_${(h >>> 0).toString(36)}`;
  } catch (_) { return ''; }
}

// ── Contexte utilisateur (lu de localStorage _user, posé au login) ───────────
function userContext() {
  try {
    const u = JSON.parse(lsGet('_user') || 'null');
    if (u && (u.id || u.userId)) {
      return { user_id: u.id || u.userId, wallet: u.wallet || u.address || '' };
    }
  } catch (_) { /* noop */ }
  return {};
}

// ── API publique ─────────────────────────────────────────────────────────────
/**
 * Émet un event. Non bloquant.
 * @param {string} eventName  cf. eventSchema back (CLIENT_ALLOWED_EVENTS)
 * @param {object} props      { path, page_title, asset_id, asset_type, category, ... }
 */
export function track(eventName, props = {}) {
  try {
    if (!eventName || !serverURL) return;
    const acq = captureAcquisition();
    const full = getConsent() === 'full';
    const uc = userContext();
    queue.push({
      event_name: eventName,
      anonymous_id: anonymousId(),
      session_id: sessionId(),
      ts: new Date().toISOString(),
      source: acq.source, medium: acq.medium, campaign: acq.campaign, term: acq.term, content: acq.content,
      referrer: acq.referrer, referrer_domain: acq.referrer_domain, landing_page: acq.landing_page,
      path: props.path || window.location.pathname,
      page_title: props.page_title || document.title,
      locale: (document.documentElement.lang || '').slice(0, 8),
      device_fp: full ? deviceFp() : '',
      ...uc,
      asset_id: props.asset_id, asset_type: props.asset_type,
      collection_id: props.collection_id, category: props.category, provider: props.provider,
      props: props.meta || undefined,
    });
    if (queue.length >= BATCH_MAX) flush();
    else scheduleFlush();
  } catch (_) { /* analytics ne casse jamais l'app */ }
}

export function trackPageView(extra = {}) {
  track('page_view', extra);
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(flush, FLUSH_MS);
}

export function flush(useBeacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length || !serverURL) return;
  const events = queue;
  queue = [];
  const payload = JSON.stringify({ events, consent: getConsent() === 'full' ? 'full' : 'anon' });
  try {
    if (useBeacon && navigator.sendBeacon) {
      // text/plain → pas de preflight ; le back reparse en JSON.
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }));
    } else {
      // Vague 4 lot 3 : fetch natif CONSERVÉ — transport beacon/keepalive requis pour le flush
      // au pagehide (axios ne supporte pas keepalive), et /analytics est monté AVANT le
      // middleware crypto côté serveur (payload en clair par contrat, cf. server/index.js l.214).
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => { /* silencieux */ });
    }
  } catch (_) { /* noop */ }
}

// Flush garanti quand l'utilisateur quitte / masque l'onglet.
let listenersBound = false;
export function initAnalytics() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  captureAcquisition();
  const onHide = () => flush(true);
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
}

export default { track, trackPageView, flush, initAnalytics, getConsent, setConsent };
